<template>
    <div class="checklist-form-wrapper">
        <div class="checklist-container">
            <div class="checklist-header">
                <div class="logo-container">
                    <IndsidersLogo />
                </div>
                <h2 class="checklist-title">{{ checklist.title || checklist.name }}</h2>
                <p class="checklist-description">{{ checklist.description }}</p>

                <div class="progress-info">
                    <div class="progress-segments">
                        <div v-for="i in totalFields" :key="i" class="progress-segment"
                            :class="{ 'filled': i <= answeredCount }"></div>
                    </div>
                    <span class="progress-text">{{ answeredCount }}/{{ totalFields }} respondidas</span>
                </div>
            </div>

            <div class="checklist-body">
                <div v-for="field in formFields" :key="field.id" class="checklist-field-card">
                    <div class="field-header">
                        <div class="field-icon-wrapper">
                            <component :is="getFieldIcon(field.type)" class="field-type-icon" />
                        </div>
                        <span class="label-text">
                            {{ getLabel(field) }}
                            <span v-if="field.isRequired" class="required-indicator">*</span>
                        </span>
                    </div>

                    <div class="field-input">
                        <!-- Text -->
                        <b-form-input v-if="field.type === 'text'" v-model="field.value"
                            :placeholder="$t('TypeHere')" />

                        <!-- Textarea -->
                        <b-form-textarea v-if="field.type === 'textarea'" v-model="field.value"
                            :placeholder="$t('TypeHere')" rows="3" />

                        <!-- Number -->
                        <b-form-input v-if="field.type === 'number'" type="number" v-model="field.value"
                            placeholder="0" />

                        <!-- Select -->
                        <v-select v-if="field.type === 'select'" v-model="field.value"
                            :options="parseOptions(field.options)" :placeholder="$t('Operator.Select')"
                            :reduce="option => option.id || option" label="label" />

                        <!-- Multiselect -->
                        <v-select v-if="field.type === 'multiselect'" v-model="field.value"
                            :options="parseOptions(field.options)" multiple :placeholder="$t('Operator.Select')"
                            :reduce="option => option.id || option" label="label" />

                        <!-- Date -->
                        <div v-if="field.type === 'date'" class="date-picker-wrapper">
                            <flat-pickr v-model="field.value" :config="getDateConfig(field.id)" class="form-control"
                                placeholder="dd/mm/aaaa" />
                        </div>

                        <!-- Date Range -->
                        <div v-if="field.type === 'daterange'" class="d-flex align-items-center date-range-container">
                            <div class="date-picker-wrapper flex-grow-1">
                                <flat-pickr v-model="field.value[0]" :config="getDateConfig(`${field.id}-start`)"
                                    class="form-control" placeholder="dd/mm/aaaa" />
                            </div>
                            <span class="mx-2 text-muted">até</span>
                            <div class="date-picker-wrapper flex-grow-1">
                                <flat-pickr v-model="field.value[1]" :config="getDateConfig(`${field.id}-end`)"
                                    class="form-control" placeholder="dd/mm/aaaa" />
                            </div>
                        </div>

                        <!-- Time -->
                        <div v-if="field.type === 'time'">
                            <b-form-input ref="timeInput" :value="field.value" type="text" placeholder="00:00"
                                @input="handleTimeInput(field, $event)" @keyup="handleTimeInput(field, $event)"
                                @keypress="onlyTimeNumbers" maxlength="5" />
                        </div>

                        <!-- Checkbox -->
                        <div v-if="field.type === 'checkbox'" class="checkbox-group">
                            <b-form-checkbox-group v-model="field.value" :options="parseOptions(field.options)" />
                        </div>

                        <!-- Location -->
                        <div v-if="field.type === 'location'" class="location-input">
                            <div class="location-inputs-container">
                                <b-form-input v-model="field.address" class="location-address-input"
                                    placeholder="Rua, nº" @input="updateLocationValue(field)" />
                                <div class="location-cep-container">
                                    <span class="location-cep-label">CEP:</span>
                                    <b-form-input v-model="field.cep" class="location-cep-input" type="text"
                                        placeholder="00000000" maxlength="9" @input="handleCepInput(field, $event)"
                                        @keypress="onlyNumbers" />
                                </div>
                            </div>
                        </div>

                        <!-- Photo -->
                        <div v-if="field.type === 'photo'" class="photo-input">
                            <div v-if="field.value" class="photo-file-info">
                                <feather-icon icon="ImageIcon" class="file-icon" />
                                <span class="file-name">{{ field.fileName }}</span>
                                <div class="remove-photo" @click="removePhoto(field)">
                                    <feather-icon icon="XIcon" />
                                </div>
                            </div>
                            <div v-else class="photo-upload-btn" @click="$refs[`fileInput-${field.id}`][0].click()">
                                <input type="file" :ref="`fileInput-${field.id}`" style="display: none" accept="image/*"
                                    @change="(e) => handleFileUpload(e, field)">
                                <feather-icon icon="CameraIcon" class="mr-2" />
                                <span>{{ $t('AddPhoto') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="checklist-footer">
                <template v-if="showPriorityButtons">
                    <b-button variant="outline-secondary" class="btn-cancel" @click="finishWithoutPriority">
                        {{ $t('Finalizar') }}
                    </b-button>
                    <b-button variant="primary" class="btn-submit" @click="requestPriority"
                        :disabled="isRequestingPriority || !checklistValuesId">
                        {{ $t('Solicitar prioridade') }}
                    </b-button>
                </template>
                <template v-else>
                    <b-button v-if="showCancelButton" variant="outline-secondary" class="btn-cancel"
                        @click="$emit('cancel')">
                        {{ $t('Cancel') }}
                    </b-button>
                    <b-button variant="primary" class="btn-submit" @click="submitChecklist" :disabled="isSubmitting">
                        {{ isSubmitting ? $t('Sending') : $t('Enviar Checklist') }}
                    </b-button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { BFormInput, BFormTextarea, BFormCheckboxGroup, BButton } from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import { http3 } from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// Importing Icons
import IndsidersLogo from "@/assets/images/icons/indsiders.svg";
import CheckboxTypeIcon from "@/assets/images/icons/Checkbox-type.svg";
import PhotoTypeIcon from "@/assets/images/icons/foto-type.svg";
import LocationTypeIcon from "@/assets/images/icons/location-type.svg";
import MultipleSelectionTypeIcon from "@/assets/images/icons/multiple-selection-type.svg";
import NumberTypeIcon from "@/assets/images/icons/number-type.svg";
import RangeDateTypeIcon from "@/assets/images/icons/Range-date-type.svg";
import SelectTypeIcon from "@/assets/images/icons/select-type.svg";
import SingleDateTypeIcon from "@/assets/images/icons/Single-date-type.svg";
import TimeTypeIcon from "@/assets/images/icons/Time-type.svg";
import TextTypeIcon from "@/assets/images/icons/text-type.svg";
import TextAreaTypeIcon from "@/assets/images/icons/text_area.svg";

export default {
    name: "ChecklistForm",
    components: {
        BFormInput,
        BFormTextarea,
        BFormCheckboxGroup,
        BButton,
        vSelect,
        flatPickr,
        FeatherIcon,
        IndsidersLogo,
        CheckboxTypeIcon,
        PhotoTypeIcon,
        LocationTypeIcon,
        MultipleSelectionTypeIcon,
        NumberTypeIcon,
        RangeDateTypeIcon,
        SelectTypeIcon,
        SingleDateTypeIcon,
        TimeTypeIcon,
        TextTypeIcon,
        TextAreaTypeIcon
    },
    props: {
        checklist: {
            type: Object,
            required: true
        },
        showCancelButton: {
            type: Boolean,
            default: true
        },
        checklistValuesId: {
            type: [String, Number],
            default: null
        },
        submissionError: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            formFields: [],
            showPriorityButtons: false,
            isRequestingPriority: false,
            isSubmitting: false,
            submittedFields: null,
            submissionTimeout: null
        };
    },
    computed: {
        totalFields() {
            return this.formFields.length;
        },
        answeredCount() {
            return this.formFields.filter(f => {
                if (f.type === 'location') {
                    return !!(f.address || f.cep);
                }
                if (Array.isArray(f.value)) return f.value.length > 0;
                return !!f.value;
            }).length;
        }
    },
    watch: {
        checklistValuesId(newVal) {
            if (newVal && this.checklist.hasValidation && this.isSubmitting) {
                this.isSubmitting = false;
                this.showPriorityButtons = true;
                if (this.submissionTimeout) {
                    clearTimeout(this.submissionTimeout);
                    this.submissionTimeout = null;
                }
            }
        },
        submissionError(newVal) {
            if (newVal && this.isSubmitting) {
                this.isSubmitting = false;
                if (this.submissionTimeout) {
                    clearTimeout(this.submissionTimeout);
                    this.submissionTimeout = null;
                }
            }
        }
    },
    created() {
        if (this.checklist && this.checklist.fields) {
            this.formFields = this.checklist.fields.map(field => {
                let initialValue = field.value;
                let address = '';
                let cep = '';

                if (field.type === 'location' && initialValue) {
                    const cepMatch = initialValue.match(/CEP:\s*(\d+)/);
                    if (cepMatch) {
                        cep = cepMatch[1];
                        address = initialValue.replace(/,\s*CEP:\s*\d+/, '').trim();
                    } else {
                        address = initialValue;
                    }
                } else if (!initialValue) {
                    if (field.type === 'checkbox' || field.type === 'multiselect') {
                        initialValue = [];
                    } else if (field.type === 'daterange') {
                        initialValue = [null, null];
                    } else if (field.type === 'location') {
                        initialValue = null;
                    } else {
                        initialValue = null;
                    }
                }

                const fieldData = {
                    ...field,
                    value: initialValue
                };

                if (field.type === 'location') {
                    fieldData.address = address;
                    fieldData.cep = cep;
                }

                console.log('fieldData', fieldData)
                return fieldData;
            });
        }
    },
    methods: {
        getLabel(field) {
            // Use name from mock/props as label
            return field.name;
        },
        getDateConfig(fieldId) {
            return {
                dateFormat: 'Y-m-d',
                altInput: true,
                altFormat: 'd/m/Y',
                static: true,
                appendTo: undefined,
                onReady: (selectedDates, dateStr, instance) => {
                    const wrapper = instance.input.closest('.date-picker-wrapper');
                    if (wrapper) {
                        instance.calendarContainer.style.position = 'absolute';
                        instance.calendarContainer.style.top = '100%';
                        instance.calendarContainer.style.left = '0';
                        instance.calendarContainer.style.right = 'auto';
                        instance.calendarContainer.style.transform = 'none';
                        wrapper.appendChild(instance.calendarContainer);
                    }
                }
            };
        },
        getTimeConfig(fieldId) {
            return {
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i',
                time_24hr: true,
                static: true,
                appendTo: undefined,
                onReady: (selectedDates, dateStr, instance) => {
                    const wrapper = instance.input.closest('.date-picker-wrapper');
                    if (wrapper) {
                        instance.calendarContainer.style.position = 'absolute';
                        instance.calendarContainer.style.top = '100%';
                        instance.calendarContainer.style.left = '0';
                        instance.calendarContainer.style.right = 'auto';
                        instance.calendarContainer.style.transform = 'none';
                        wrapper.appendChild(instance.calendarContainer);
                    }
                }
            };
        },
        getFieldIcon(type) {
            const icons = {
                text: 'TextTypeIcon',
                textarea: 'TextAreaTypeIcon',
                number: 'NumberTypeIcon',
                select: 'SelectTypeIcon',
                multiselect: 'MultipleSelectionTypeIcon',
                date: 'SingleDateTypeIcon',
                daterange: 'RangeDateTypeIcon',
                time: 'TimeTypeIcon',
                checkbox: 'CheckboxTypeIcon',
                location: 'LocationTypeIcon',
                photo: 'PhotoTypeIcon'
            };

            return icons[type] || 'CircleIcon';
        },
        parseOptions(options) {
            if (!options) return [];
            if (Array.isArray(options)) return options;
            try {
                return JSON.parse(options);
            } catch (e) {
                return options.split(',').map(opt => opt.trim());
            }
        },
        updateLocationValue(field) {
            const parts = [];
            if (field.address) {
                parts.push(field.address);
            }
            if (field.cep) {
                parts.push(`CEP: ${field.cep}`);
            }
            field.value = parts.length > 0 ? parts.join(', ') : null;
        },
        handleCepInput(field, event) {
            const value = event.target.value.replace(/\D/g, '').slice(0, 9);
            field.cep = value;
            this.updateLocationValue(field);
        },
        onlyNumbers(event) {
            const char = String.fromCharCode(event.which);
            const currentValue = event.target.value || '';
            if (!/[0-9]/.test(char) || currentValue.replace(/\D/g, '').length >= 9) {
                event.preventDefault();
            }
        },
        handleTimeInput(field, event) {
            let value = event.target.value.replace(/\D/g, '');
            let formatted = '';

            if (value.length === 0) {
                formatted = '';
            } else if (value.length === 1) {
                formatted = value;
            } else if (value.length === 2) {
                let hours = parseInt(value, 10);
                if (hours > 23) hours = 23;
                formatted = String(hours).padStart(2, '0') + ':';
            } else {
                let hours = parseInt(value.slice(0, 2), 10);
                let minutesStr = value.slice(2, 4);

                if (hours > 23) hours = 23;

                if (minutesStr.length === 1) {
                    formatted = String(hours).padStart(2, '0') + ':' + minutesStr;
                } else {
                    let minutes = parseInt(minutesStr, 10);
                    if (minutes > 59) minutes = 59;
                    formatted = String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
                }
            }

            field.value = formatted;

            if (event.target.value !== formatted) {
                event.target.value = formatted;
                const cursorPos = formatted.length;
                this.$nextTick(() => {
                    event.target.setSelectionRange(cursorPos, cursorPos);
                });
            }
        },
        onlyTimeNumbers(event) {
            const char = String.fromCharCode(event.which);
            if (!/[0-9]/.test(char)) {
                event.preventDefault();
            }
        },
        handleFileUpload(event, field) {
            const file = event.target.files[0];
            if (file && file.type.includes("image/")) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    field.value = reader.result;
                    this.$set(field, 'fileName', file.name);
                };
            }
        },
        removePhoto(field) {
            field.value = null;
            field.fileName = null;
        },
        submitChecklist() {
            const invalid = this.formFields.find(f => {
                if (!f.isRequired) return false;
                if (f.type === 'location') {
                    return !(f.address || f.cep);
                }
                return !f.value;
            });
            if (invalid) {
                this.$toast && this.$toast.error(`O campo ${invalid.name} é obrigatório.`);
                return;
            }

            this.isSubmitting = true;

            if (this.submissionTimeout) {
                clearTimeout(this.submissionTimeout);
            }

            this.submissionTimeout = setTimeout(() => {
                if (this.isSubmitting && !this.checklistValuesId) {
                    this.isSubmitting = false;
                }
            }, 30000);

            if (this.checklist.hasValidation) {
                this.submittedFields = this.formFields;
                this.$emit('submit', this.formFields);
            } else {
                this.$emit('submit', this.formFields);
            }
        },
        finishWithoutPriority() {
            this.$emit('finish');
        },
        async requestPriority() {
            if (!this.checklistValuesId) {
                this.$emit('finish');
                return;
            }

            this.isRequestingPriority = true;
            try {
                await http3.put(
                    `api/myquality/checklistValues/${this.checklistValuesId}`,
                    { isPriority: true }
                );
                this.showToast(
                    this.$t('Sucesso'),
                    'CheckIcon',
                    this.$t('Prioridade solicitada com sucesso'),
                    'success'
                );
                this.$emit('finish');
            } catch (error) {
                console.error("Error requesting priority:", error);
                this.showToast(
                    this.$t('Erro'),
                    'AlertTriangleIcon',
                    this.$t('Erro ao solicitar prioridade'),
                    'danger'
                );
                this.$emit('finish');
            } finally {
                this.isRequestingPriority = false;
            }
        },
        showToast(title, icon, text, variant) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title,
                    icon,
                    text,
                    variant
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@import '~vue-select/dist/vue-select.css';

.checklist-form-wrapper {
    background: #fcfcfc;
    min-height: 100%;
    padding: 24px;
    display: flex;
    justify-content: center;
}

.checklist-container {
    width: 100%;
    max-width: 900px; // Adjusted width based on prints
    display: flex;
    flex-direction: column;
}

.checklist-header {
    margin-bottom: 32px;

    .logo-container {
        margin-bottom: 24px;

        svg {
            height: 40px; // Adjust size as needed
            width: auto;
        }
    }

    .checklist-title {
        font-size: 24px;
        font-weight: 600;
        color: #4c4541;
        margin-bottom: 8px;
    }

    .checklist-description {
        font-size: 16px;
        color: #998f8a;
        margin-bottom: 32px;
    }

    .progress-info {
        display: flex;
        align-items: center;

        .progress-segments {
            flex-grow: 1;
            display: flex;
            gap: 8px; // Gap between segments
            margin-right: 16px;

            .progress-segment {
                flex: 1;
                height: 6px;
                background: #e0e0e0;
                border-radius: 3px;

                &.filled {
                    background: #ff9f43;
                }
            }
        }

        .progress-text {
            font-size: 14px;
            color: #998f8a;
            white-space: nowrap;
        }
    }
}

.checklist-body {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 40px;
}

.checklist-field-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px; // Reduced radius to match print
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); // Subtle shadow

    .field-header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .field-icon-wrapper {
            margin-right: 16px;
            display: flex;
            align-items: center;
            justify-content: center;

            .field-type-icon {
                width: 24px;
                height: 24px;
            }

            // Style adjustments for imported SVGs if needed
            svg {
                max-width: 24px;
                max-height: 24px;
            }
        }

        .label-text {
            font-size: 14px;
            color: #4c4541;
            font-weight: 400;
            display: inline-flex;
            align-items: baseline;

            .required-indicator {
                color: #ea5455;
                margin-left: 4px;
                font-weight: 600;
                font-size: 20px;
                line-height: 1;
            }
        }
    }

    .field-input {

        // Customize input appearance if needed
        input,
        textarea,
        .form-control {
            background-color: #ffffff !important;
        }

        .flatpickr-input {
            background-color: #ffffff !important;
            background: #ffffff !important;
            border: 1px solid #d8d6de;
            border-radius: 5px;
            padding: 0.438rem 1rem;
            width: 100% !important;

            &:focus {
                border-color: #974900;
                box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.25);
                background-color: #ffffff !important;
                background: #ffffff !important;
            }
        }

        .checkbox-group {
            display: flex;
            gap: 16px;

            ::v-deep .custom-control-input:checked~.custom-control-label::before,
            ::v-deep .custom-control-input:active~.custom-control-label::before {
                background-color: #974900 !important;
                border-color: #974900 !important;
            }
        }
    }
}

.checklist-footer {
    display: flex;
    gap: 16px;
    margin-top: auto;
    padding-bottom: 24px;

    button {
        padding: 14px 32px;
        font-weight: 500;
        min-height: 48px;
    }

    .btn-cancel {
        flex: 1;
        max-width: 200px;
        background: #fff !important;
        border: 1px solid #974900 !important;
        color: #974900 !important;

        &:hover {
            background: #FFEDE2 !important;
        }

        &:active {
            background: #FFDBC4 !important;
        }
    }

    .btn-submit {
        flex: 1;
        background: #7a3b00 !important;
        border: none !important;
        color: #fff !important;
        box-shadow: none !important;

        &:hover,
        &:focus {
            background: #8a4a0f !important;
            box-shadow: none !important;
        }

        &:active,
        &.active {
            background: #9a5a1f !important;
            box-shadow: none !important;
        }
    }
}

// Specific styles
.photo-input {
    .photo-upload-btn {
        border: 1px solid #d8d6de;
        border-radius: 5px;
        padding: 0.438rem 1rem;
        min-height: 38px;
        display: flex;
        align-items: center;
        cursor: pointer;
        background: #fff;
        color: #b9b9c3;

        &:hover {
            border-color: #974900;
        }
    }

    .photo-file-info {
        border: 1px solid #d8d6de;
        border-radius: 5px;
        padding: 0.438rem 1rem;
        min-height: 38px;
        display: flex;
        align-items: center;
        background: #fff;

        .file-icon {
            color: #974900;
            margin-right: 12px;
            flex-shrink: 0;
        }

        .file-name {
            flex: 1;
            color: #4c4541;
            font-size: 14px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .remove-photo {
            cursor: pointer;
            color: #b9b9c3;
            margin-left: 12px;
            flex-shrink: 0;

            &:hover {
                color: #ea5455;
            }
        }
    }
}

.location-input {
    .location-inputs-container {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .location-address-input {
        flex: 2;
        width: 66.66%;
    }

    .location-cep-container {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .location-cep-label {
        font-size: 14px;
        color: #4c4541;
        white-space: nowrap;
    }

    .location-cep-input {
        flex: 1;
        min-width: 0;
    }
}

.field-input::v-deep .v-select {
    .vs__dropdown-toggle {
        border: 1px solid #d8d6de !important;
        border-radius: 5px !important;
        padding: 4px 0 !important;
        background: #fff !important;
        transition: all 0.2s ease !important;
        min-height: 38px !important;
    }

    .vs__search,
    .vs__search:focus {
        margin: 4px 0 0 !important;
        padding: 0 7px !important;
    }

    .vs__search::placeholder {
        color: #b9b9c3 !important;
    }

    .vs__selected {
        margin: 4px 2px 0 !important;
        padding: 4px 10px !important;
        border-radius: 4px !important;
        background: #974900 !important;
        color: #fff !important;
        border: none !important;
    }

    .vs__deselect {
        fill: #fff !important;
    }

    .vs__deselect:hover {
        fill: #fff !important;
    }

    .vs__open-indicator {
        fill: #5e5873 !important;
        transition: transform 0.2s ease !important;
    }

    .vs__actions {
        padding-right: 10px !important;
    }

    .vs__clear {
        fill: #5e5873 !important;
    }

    &.vs--single.vs--open .vs__selected {
        opacity: 0.4 !important;
    }

    &.vs--open .vs__dropdown-toggle {
        border-color: #974900 !important;
        box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.25) !important;
    }

    &.vs--open .vs__open-indicator {
        transform: rotate(180deg) !important;
    }

    .vs__dropdown-menu {
        border: 1px solid #974900 !important;
        border-radius: 5px !important;
        box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
        margin-top: 4px !important;
    }

    .vs__dropdown-option {
        padding: 10px 15px !important;
        color: #974900 !important;
        font-size: 14px !important;
    }

    .vs__dropdown-option--highlight {
        background: #fff5eb !important;
        color: #974900 !important;
    }

    .vs__dropdown-option--selected {
        background: #974900 !important;
        color: #fff !important;
    }

    .vs__dropdown-option--selected.vs__dropdown-option--highlight {
        background: #7a3b00 !important;
        color: #fff !important;
    }
}

.date-picker-wrapper {
    position: relative;
    width: 100% !important;
}

::v-deep .date-picker-wrapper .flatpickr-calendar {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    margin-top: 4px;
    z-index: 999;
}

::v-deep .flatpickr-day.selected,
::v-deep .flatpickr-day.selected:hover {
    background: #974900 !important;
    border-color: #974900 !important;
    color: #fff !important;
}

::v-deep .flatpickr-day:hover {
    background: #fff5eb !important;
    border-color: #974900 !important;
    color: #974900 !important;
}

::v-deep .flatpickr-wrapper {
    width: 100% !important;
    display: block !important;

    input,
    input[readonly],
    .form-control,
    .form-control[readonly],
    .flatpickr-input {
        background-color: #ffffff !important;
        background: #ffffff !important;
    }
}

::v-deep .flatpickr-input,
::v-deep .flatpickr-input[readonly],
::v-deep .flatpickr-wrapper .flatpickr-input,
::v-deep .flatpickr-wrapper input.flatpickr-input,
::v-deep .flatpickr-wrapper input[readonly],
::v-deep .flatpickr-wrapper .form-control[readonly],
::v-deep .flatpickr-wrapper input.form-control[readonly],
::v-deep .date-picker-wrapper .flatpickr-input,
::v-deep .date-picker-wrapper input.flatpickr-input,
::v-deep .date-picker-wrapper .form-control[readonly],
::v-deep .date-picker-wrapper input.form-control[readonly],
::v-deep .flatpickr-input~.form-control[readonly] {
    background-color: #ffffff !important;
    background: #ffffff !important;
    border: 1px solid #d8d6de !important;
    border-radius: 5px !important;
    padding: 0.438rem 1rem !important;
    width: 100% !important;

    &:focus {
        border-color: #974900 !important;
        box-shadow: 0 0 0 0.2rem rgba(151, 73, 0, 0.25) !important;
        background-color: #ffffff !important;
        background: #ffffff !important;
    }
}
</style>

<style>
.checklist-form-wrapper .flatpickr-wrapper input,
.checklist-form-wrapper .flatpickr-wrapper input[readonly],
.checklist-form-wrapper .flatpickr-wrapper .form-control,
.checklist-form-wrapper .flatpickr-wrapper .form-control[readonly],
.checklist-form-wrapper .flatpickr-wrapper .flatpickr-input,
.checklist-form-wrapper .flatpickr-input~.form-control[readonly],
.checklist-form-wrapper .date-picker-wrapper .form-control[readonly] {
    background-color: #ffffff !important;
    background: #ffffff !important;
}

.checklist-form-wrapper .flatpickr-day.selected,
.checklist-form-wrapper .flatpickr-day.selected:hover {
    background: #974900 !important;
    border-color: #974900 !important;
    color: #fff !important;
}

.checklist-form-wrapper [dir] .btn-primary.btn-submit,
.checklist-form-wrapper .btn-primary.btn-submit {
    background: #7a3b00 !important;
    border: none !important;
    color: #fff !important;
    box-shadow: none !important;

    &:hover,
    &:focus {
        background: #8a4a0f !important;
        box-shadow: none !important;
    }

    &:active,
    &.active {
        background: #9a5a1f !important;
        box-shadow: none !important;
    }
}

.checklist-form-wrapper [dir] .btn-outline-secondary.btn-cancel,
.checklist-form-wrapper .btn-outline-secondary.btn-cancel {
    background: #fff !important;
    border: 1px solid #974900 !important;
    color: #974900 !important;

    &:hover {
        background: #FFEDE2 !important;
    }

    &:active {
        background: #FFDBC4 !important;
    }
}
</style>